import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { useNavigate } from "react-router-dom";
import * as DashboardAPI from "api/Dashboard";
import * as QuotesAPI from "api/Quotes";
import QuoteRow from "./QuoteRow";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import { QuestionMarkIcon, CheckMarkIcon, SubmitIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";
import PriceLockModal from "components/PriceLockModal";
import QuoteAcceptedModal from "components/QuoteAcceptedModal";

const COMPONENT_HEIGHT = 500;

interface PendingQuote {
  id: string;
  quoteNumber: string;
  projectName: string;
  amount: number;
  sentDate: string;
  daysPending: number;
  urgency: "critical" | "warning" | "normal";
  status?: number;
  priceLockDepositStatus?: string | null;
  priceLockDepositAmount?: number | null;
  priceLockExpiresAt?: string | null;
  expiresAt?: string | null;
}

const QuotesAwaitingResponse: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [quotes, setQuotes] = useState<PendingQuote[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmAcceptModalOpen, setConfirmAcceptModalOpen] = useState<boolean>(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [quoteAccepted, setQuoteAccepted] = useState<boolean>(false);
  const [acceptedQuote, setAcceptedQuote] = useState<any>(null);

  // Price lock modal state
  const [lockTarget, setLockTarget] = useState<PendingQuote | null>(null);
  const [lockInitialStep, setLockInitialStep] = useState<"choose" | "pay">("choose");

  // Confirm project modal (for deposit-received quotes)
  const [confirmProjectQuote, setConfirmProjectQuote] = useState<PendingQuote | null>(null);
  const [confirmingProject, setConfirmingProject] = useState(false);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const data = await DashboardAPI.getClientPendingQuotes(5);
      if (data) {
        setQuotes(data.quotes);
        setTotalCount(data.totalCount);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching pending quotes:", err);
      setError("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleViewAll = () => {
    navigate("/quotes");
  };

  // Standard accept flow (status 0 quotes)
  const handleAccept = async (quoteId: string) => {
    setSelectedQuoteId(quoteId);
    setConfirmAcceptModalOpen(true);
  };

  const confirmAccept = async () => {
    if (!selectedQuoteId) return;

    setAccepting(true);
    try {
      const res = await QuotesAPI.acceptOrDeny({
        data: { id: selectedQuoteId },
        update: { status: 1, discountFlag: false }
      });

      if (res.quote) {
        toast.add(
          "Quote accepted.",
          "var(--theme-ui-colors-success)"
        );
        setAcceptedQuote(res.quote);
        setQuoteAccepted(true);
        await fetchQuotes();
      } else {
        toast.add("Failed to accept quote.", "var(--theme-ui-colors-danger)");
      }
    } catch (err) {
      console.error("Error accepting quote:", err);
      toast.add("An error occurred while accepting the quote.", "var(--theme-ui-colors-danger)");
    } finally {
      setAccepting(false);
    }
  };

  const cancelAcceptConfirm = () => {
    setConfirmAcceptModalOpen(false);
    setSelectedQuoteId(null);
    setQuoteAccepted(false);
    setAcceptedQuote(null);
  };

  const handleViewOrPayInvoice = async () => {
    if (!acceptedQuote?.invoice?.id) {
      toast.add("Invoice not found. Please refresh the page.", "var(--theme-ui-colors-danger)");
      return;
    }
    navigate("/invoices/" + acceptedQuote.invoice.id + "?pay=1");
    cancelAcceptConfirm();
  };

  const handleReview = (quoteId: string) => {
    window.open("/quotes/" + quoteId, "_blank");
  };

  // Lock Price flow — opens PriceLockModal at step 1
  const handleLockPrice = (quoteId: string) => {
    const quote = quotes.find((q) => q.id === quoteId);
    if (!quote) return;
    setLockInitialStep("choose");
    setLockTarget(quote);
  };

  // Pay Now flow — opens PriceLockModal at step 2 (payment)
  const handlePayNow = (quoteId: string) => {
    const quote = quotes.find((q) => q.id === quoteId);
    if (!quote) return;
    setLockInitialStep("pay");
    setLockTarget(quote);
  };

  const handleLockComplete = () => {
    setLockTarget(null);
    fetchQuotes();
  };

  // Confirm & Start Project flow (deposit-received quotes)
  const handleConfirmProject = (quoteId: string) => {
    const quote = quotes.find((q) => q.id === quoteId);
    if (!quote) return;
    setConfirmProjectQuote(quote);
  };

  const doConfirmProject = async () => {
    if (!confirmProjectQuote) return;
    setConfirmingProject(true);
    try {
      const res = await QuotesAPI.acceptOrDeny({
        data: { id: confirmProjectQuote.id },
        update: { status: 1, discountFlag: false }
      });
      if (res.quote) {
        toast.add("Project confirmed and created.", "var(--theme-ui-colors-success)");
        setConfirmProjectQuote(null);
        await fetchQuotes();
        // The accept response carries the project on the quote, not top level.
        const newProjectId = res.quote?.project?.id ?? res.project?.id;
        if (newProjectId) {
          navigate("/projects/" + newProjectId);
        }
      } else {
        toast.add("Failed to confirm project.", "var(--theme-ui-colors-danger)");
      }
    } catch (err) {
      console.error("Error confirming project:", err);
      toast.add("An error occurred.", "var(--theme-ui-colors-danger)");
    } finally {
      setConfirmingProject(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ padding: "24px" }}>
        <P fontFamily="Poppins" fontSize="14px" color="#6B7280">
          Loading quotes...
        </P>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: "24px" }}>
        <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.danger}>
          {error}
        </P>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        borderRadius: "8px",
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        minHeight: COMPONENT_HEIGHT + "px",
        display: "flex",
        flexDirection: "column",
        flex: "0 1 calc(50% - 8px)",
      }}
    >
      {/* Section Header */}
      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
          flexShrink: 0,
        }}
      >
        <P
          fontFamily="Poppins"
          fontSize="14px"
          fontWeight="600"
          color="black"
          marginTop="0"
          marginBottom="0"
        >
          Quotes Awaiting Response ({totalCount})
        </P>
        {totalCount > 5 && (
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={handleViewAll}
          >
            View All
          </BasicButton>
        )}
      </Flex>

      {/* Quotes List */}
      <Box sx={{ padding: "16px", flex: 1, overflowY: "auto" }}>
        {quotes.length === 0 ? (
          <Box
            sx={{
              padding: "32px",
              textAlign: "center",
            }}
          >
            <P fontFamily="Poppins" fontSize="14px" color="#6B7280" marginTop="0" marginBottom="0">
              No pending quotes
            </P>
          </Box>
        ) : (
          quotes.map((quote) => (
            <QuoteRow
              key={quote.id}
              id={quote.id}
              quoteNumber={quote.quoteNumber}
              projectName={quote.projectName}
              amount={quote.amount}
              sentDate={quote.sentDate}
              daysPending={quote.daysPending}
              urgency={quote.urgency}
              status={quote.status}
              priceLockDepositStatus={quote.priceLockDepositStatus}
              priceLockDepositAmount={quote.priceLockDepositAmount}
              onAccept={handleAccept}
              onReview={handleReview}
              onLockPrice={handleLockPrice}
              onPayNow={handlePayNow}
              onConfirmProject={handleConfirmProject}
            />
          ))
        )}
      </Box>

      {/* Accept Confirmation Modal */}
      {confirmAcceptModalOpen && !quoteAccepted && (
        <BasicModal
          title="Accept Quote?"
          cancel_click={cancelAcceptConfirm}
          cancel_text="No, cancel"
          $submitting={accepting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={confirmAccept}
              $submitting={accepting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!accepting && <CheckMarkIcon fill={"#fff"} />}
              {"Accept quote"}
            </BasicButton>
          }
        >
          <P lineHeight="22px" marginBottom="0px">
            Are you sure you want to accept this quote?
          </P>
        </BasicModal>
      )}

      {/* Compact post-accept window + PO / site-map upload (components/QuoteAcceptedModal). */}
      {confirmAcceptModalOpen && quoteAccepted && acceptedQuote && (
        <QuoteAcceptedModal
          quote={acceptedQuote}
          onClose={cancelAcceptConfirm}
          onPay={handleViewOrPayInvoice}
        />
      )}

      {/* Price Lock Modal */}
      {lockTarget && (
        <PriceLockModal
          quote={{
            id: lockTarget.id,
            total: lockTarget.amount,
            priceLockDepositAmount: lockTarget.priceLockDepositAmount || undefined,
            expiresAt: lockTarget.expiresAt || undefined,
            status: lockTarget.status || 0,
            priceLockDepositStatus: lockTarget.priceLockDepositStatus || undefined,
          }}
          onComplete={handleLockComplete}
          onCancel={() => setLockTarget(null)}
          initialStep={lockInitialStep}
        />
      )}

      {/* Confirm Project Modal (deposit-received quotes) */}
      {confirmProjectQuote && (
        <BasicModal
          title="Confirm Project"
          cancel_click={() => setConfirmProjectQuote(null)}
          cancel_text="Cancel"
          $submitting={confirmingProject}
          size={"sm"}
          icon={<CheckMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={doConfirmProject}
              $submitting={confirmingProject}
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!confirmingProject && <SubmitIcon fill={"#fff"} direction="up" />}
              Confirm & Start Project
            </BasicButton>
          }
        >
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", lineHeight: "22px" }}>
            <p style={{ margin: "0 0 12px 0" }}>
              Your deposit of <strong>{"$" + (confirmProjectQuote.priceLockDepositAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong> will be credited to your invoice.
            </p>
            <div style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: "6px", padding: "12px 16px", marginBottom: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "#718096" }}>Quote Total</span>
                <span style={{ fontWeight: 600 }}>{"$" + (confirmProjectQuote.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "#38A169" }}>Deposit Paid</span>
                <span style={{ color: "#38A169", fontWeight: 600 }}>{"-$" + (confirmProjectQuote.priceLockDepositAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #E2E8F0", paddingTop: "8px", marginTop: "4px" }}>
                <span style={{ fontWeight: 700 }}>Remaining Balance</span>
                <span style={{ fontWeight: 700 }}>{"$" + ((confirmProjectQuote.amount || 0) - (confirmProjectQuote.priceLockDepositAmount || 0)).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <p style={{ margin: 0, color: "#718096", fontSize: "13px" }}>
              A project and invoice will be created. Your deposit will be automatically credited.
            </p>
          </div>
        </BasicModal>
      )}
    </Box>
  );
};

export default QuotesAwaitingResponse;
